import type { ScmConnection } from '@/api/schemas';
import { formatDateTime, timeAgo } from '@/lib/format';
import { useNow } from '@/lib/useNow';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/badges';
import { TestResultChecks } from '@/features/integrations/TestResultChecks';
import { connectionHealth, connectionPath, shownLastError } from './scmConnectionModel';
import type { ScmConnectionActions } from './useScmConnectionActions';

function ConnectionSummary({ c, actions, now }: { c: ScmConnection; actions: ScmConnectionActions; now: number }) {
  const health = connectionHealth(c, actions.testing === c.id);
  const lastError = shownLastError(c);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate text-sm font-semibold text-foreground">{c.name}</span>
        <StatusPill status={health.status} label={health.label} />
      </div>
      <div className="truncate font-mono text-xs text-subtle">
        {connectionPath(c)}
        {c.health.checkedAt ? <span title={formatDateTime(c.health.checkedAt)}> · checked {timeAgo(c.health.checkedAt, now)}</span> : null}
      </div>
      {lastError ? <div className="mt-0.5 truncate text-xs text-failed" title={lastError}>{lastError}</div> : null}
    </div>
  );
}

function ConnectionButtons({ c, actions }: { c: ScmConnection; actions: ScmConnectionActions }) {
  return (
    <div className="flex items-center gap-2">
      <GatedButton size="sm" variant="outline" data-testid="scm-connection-test" can={actions.can} loading={actions.testing === c.id} onClick={() => void actions.runTest(c)}>
        <Icon name="bolt" size={16} />
        Test connection
      </GatedButton>
      <GatedButton size="sm" variant="ghost" data-testid="scm-connection-replace-token" can={actions.can} onClick={() => actions.replaceToken(c)}>
        <Icon name="key" size={16} />
        Replace token
      </GatedButton>
    </div>
  );
}

function ConnectionRow({ c, actions, now }: { c: ScmConnection; actions: ScmConnectionActions; now: number }) {
  const result = actions.results[c.id];
  return (
    <li data-testid={`scm-connection-${c.repository ? `${c.workspace}-${c.repository}` : c.workspace}`} data-status={c.status} className="flex flex-col gap-2 rounded-lg border bg-secondary/40 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-card text-foreground">
          <Icon name="account_tree" size={18} />
        </span>
        <ConnectionSummary c={c} actions={actions} now={now} />
        <ConnectionButtons c={c} actions={actions} />
      </div>
      {result ? <TestResultChecks result={result} accountName={c.name} /> : null}
    </li>
  );
}

/** The connected Bitbucket workspaces: status, last check, Test connection and Replace token (both need admin). */
export function ScmConnectionsList({ connections, actions }: { connections: ScmConnection[]; actions: ScmConnectionActions }) {
  const now = useNow(30_000);
  return (
    <ul className="flex flex-col gap-2" data-testid="scm-connections">
      {connections.map((c) => (
        <ConnectionRow key={c.id} c={c} actions={actions} now={now} />
      ))}
    </ul>
  );
}
